import { Button, Dialog, useTranslation, useTranslations } from "@harea/component";
import { useEffect, useMemo, useState } from "react";

export type InsufficientQuotaAlternative =
  | "negativeBalance"
  | "deductFromPay"
  | "exception";

export type InsufficientQuotaError = {
  error?: string;
  requestedDays?: number;
  remainingQuota?: number;
  deficit?: number;
  alternatives?: Array<string | { value?: string; label?: string; [key: string]: any }>;
};

type InsufficientQuotaOption = {
  value: string;
  label: string;
};

export const InsufficientQuotaDialog = ({
  open,
  error,
  isLoading,
  onClose,
  onConfirm,
}: Props) => {
  const [
    TITLE_KEY,
    MESSAGE_KEY,
    REQUESTED_KEY,
    REMAINING_KEY,
    DEFICIT_KEY,
    NEGATIVE_BALANCE_KEY,
    DEDUCT_FROM_PAY_KEY,
    EXCEPTION_KEY,
    CONFIRM,
    CANCEL,
  ] = useTranslations(i18n);
  const t = useTranslation();

  const tr = (key: string, fallback: string) => {
    const value = t(key);
    return value === key ? fallback : value;
  };

  const labels = useMemo(
    () => ({
      negativeBalance: tr(NEGATIVE_BALANCE_KEY, "Accepter un solde négatif"),
      deductFromPay: tr(DEDUCT_FROM_PAY_KEY, "Déduire de la paie"),
      exception: tr(EXCEPTION_KEY, "Caractère exceptionnel"),
    }),
    [t, NEGATIVE_BALANCE_KEY, DEDUCT_FROM_PAY_KEY, EXCEPTION_KEY],
  );

  const options = useMemo<InsufficientQuotaOption[]>(() => {
    const mapBackendType = (type: string): string => {
      switch (type) {
        case 'NEGATIVE_BALANCE': return 'negativeBalance';
        case 'DEDUCT_FROM_PAY': return 'deductFromPay';
        case 'EXCEPTIONAL_APPROVAL': return 'exception';
        default: return type;
      }
    };

    if (Array.isArray(error?.alternatives) && error.alternatives.length > 0) {
      return error.alternatives
        .map((alt) => {
          if (typeof alt === "string") {
            const mappedValue = mapBackendType(alt);
            return { value: mappedValue, label: labels[mappedValue] ?? alt };
          }
          const rawValue = alt?.value ?? alt?.key ?? alt?.type ?? alt?.designation ?? alt?.name ?? "";
          const value = mapBackendType(rawValue);
          const label =
            alt?.label ?? alt?.description ?? alt?.designation ?? alt?.name ?? labels[value] ?? rawValue;
          return value ? { value, label } : undefined;
        })
        .filter((opt): opt is InsufficientQuotaOption => !!opt);
    }
    return (["negativeBalance", "deductFromPay", "exception"] as const).map(
      (value) => ({ value, label: labels[value] }),
    );
  }, [error?.alternatives, labels]);

  const [selected, setSelected] = useState<string>(
    options[0]?.value ?? "negativeBalance",
  );

  useEffect(() => {
    if (options?.length && !options.some((opt) => opt.value === selected)) {
      setSelected(options[0].value);
    }
  }, [options, selected]);

  useEffect(() => {
    if (options?.length && error) {
      setSelected(options[0].value);
    }
  }, [error]);

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} title={tr(TITLE_KEY, "Quota insuffisant")}>
      <div className="flex flex-col gap-5">
        <div>
          <p>{tr(MESSAGE_KEY, "Le quota disponible est insuffisant pour valider cette demande.")}</p>
          {(error?.requestedDays !== undefined ||
            error?.remainingQuota !== undefined ||
            error?.deficit !== undefined) && (
            <p className="text-xs text-muted mt-2 flex gap-4">
              {error?.requestedDays !== undefined && (
                <span>
                  {tr(REQUESTED_KEY, "Demandé")} : {error.requestedDays} j
                </span>
              )}
              {error?.remainingQuota !== undefined && (
                <span>
                  {tr(REMAINING_KEY, "Restant")} : {error.remainingQuota} j
                </span>
              )}
              {error?.deficit !== undefined && (
                <span>
                  {tr(DEFICIT_KEY, "Déficit")} : {error.deficit} j
                </span>
              )}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-3">
          {options.map((opt) => (
            <label
              key={opt.value}
              className="flex items-center gap-3 rounded border border-default px-3 py-2 cursor-pointer"
            >
              <input
                type="radio"
                name="insufficient-quota-alternative"
                value={opt.value}
                checked={selected === opt.value}
                onChange={() => setSelected(opt.value)}
                disabled={isLoading}
              />
              <span className="text-sm">{opt.label}</span>
            </label>
          ))}
        </div>

        <div className="flex justify-end gap-2">
          <Button
            className="btn-default"
            label={CANCEL}
            onClick={handleClose}
            disabled={isLoading}
          />
          <Button
            className="btn-primary"
            label={CONFIRM}
            onClick={() => onConfirm(selected)}
            disabled={isLoading}
          />
        </div>
      </div>
    </Dialog>
  );
};

const i18n = [
  "Harea.Project.DayOff.Validate.InsufficientQuota.Title",
  "Harea.Project.DayOff.Validate.InsufficientQuota.Message",
  "Harea.Project.DayOff.Validate.InsufficientQuota.RequestedDays",
  "Harea.Project.DayOff.Validate.InsufficientQuota.RemainingQuota",
  "Harea.Project.DayOff.Validate.InsufficientQuota.Deficit",
  "Harea.Project.DayOff.Validate.InsufficientQuota.NegativeBalance",
  "Harea.Project.DayOff.Validate.InsufficientQuota.DeductFromPay",
  "Harea.Project.DayOff.Validate.InsufficientQuota.Exception",
  "std_confirmation",
  "std_cancel",
];

type Props = {
  open: boolean;
  error?: InsufficientQuotaError | null;
  isLoading?: boolean;
  onClose: () => void;
  onConfirm: (alternative: string) => void;
};